import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, startWith, switchMap, take } from 'rxjs';

import { MediaQueryService } from '@fcom/common/services';
import { ConfigService, NativeBridgeService } from '@fcom/core/services';
import { TripType } from '@fcom/core/interfaces';
import { isFinnairNorraOrWetLease } from '@fcom/core/utils';
import { cartData, cartDataSelector } from '@fcom/booking/store/selectors';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';
import { Breakpoint, MultivariateTestId, TestVariant } from '@fcom/common/interfaces';
import { selectActiveTab } from '@fcom/booking-widget/store';
import { WidgetTab } from '@fcom/booking-widget/interfaces';

import { getTripTypeFromBounds, isAwardBooking } from '../utils/common-booking.utils';
import { isFlight } from '../utils/flight.utils';

const MOBILE_VIEW_BREAKPOINT: Breakpoint = 'tablet_down';

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartService {
  configService = inject(ConfigService);
  multivariateTestService = inject(MultivariateTestService);
  nativeBridgeService = inject(NativeBridgeService);
  store$ = inject(Store);
  private readonly mediaQueryService = inject(MediaQueryService);

  readonly isEnabled: Signal<boolean> = signal<boolean>(false);
  readonly isOpen = signal<boolean>(false);
  readonly showDesktopCart = computed(
    () => this.isEnabled() && !this.mediaQueryService.isBreakPoint(MOBILE_VIEW_BREAKPOINT)()
  );
  readonly showModalCart = computed(
    () => this.isEnabled() && this.mediaQueryService.isBreakPoint(MOBILE_VIEW_BREAKPOINT)()
  );
  readonly isAllowedToShowCtaInModal = signal(true);

  private readonly isTestVariantB: WritableSignal<boolean> = signal<boolean>(false);
  private readonly isAward: Signal<boolean> = signal<boolean>(false);
  private readonly cart = this.store$.selectSignal(cartDataSelector);

  private readonly tripType: Signal<TripType | undefined> = computed(() => {
    const bounds = this.cart()?.bounds;
    return bounds?.length ? getTripTypeFromBounds(bounds) : undefined;
  });

  private readonly allBoundsDirect: Signal<boolean> = computed(() =>
    (this.cart()?.bounds ?? []).every((bound) => bound.flights === 1)
  );

  // Norra and wet leases ("Iberia for Finnair") count as Finnair, the same way the cart contents treat them.
  private readonly allFlightsOperatedByFinnairGroup: Signal<boolean> = computed(() =>
    (this.cart()?.bounds ?? [])
      .flatMap((bound) => (bound.itinerary ?? []).filter(isFlight))
      .every((flight) => isFinnairNorraOrWetLease(flight.operatingAirline?.name, flight.operatingAirline?.code))
  );

  constructor() {
    if (!this.configService.cfg.enableShoppingCart) {
      return;
    }

    this.isAward = toSignal(this.isAward$(), { requireSync: true });

    this.store$
      .pipe(
        cartData(),
        filter(Boolean),
        switchMap(() =>
          this.multivariateTestService.getTestVariant(MultivariateTestId.SHOPPING_CART_THREE).pipe(
            take(1),
            map(({ variant }) => variant === TestVariant.B)
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe((isVariantB) => this.isTestVariantB.set(isVariantB));

    const transfersAllowed = this.configService.cfg.enableShoppingCartForTransfers;
    const nonFinnairFlightsAllowed = this.configService.cfg.enableShoppingCartForNonFinnairFlights;

    this.isEnabled = computed(
      () =>
        this.isTestVariantB() &&
        !this.isAward() &&
        this.tripType() !== TripType.MULTICITY &&
        (transfersAllowed || this.allBoundsDirect()) &&
        (nonFinnairFlightsAllowed || this.allFlightsOperatedByFinnairGroup()) &&
        !this.nativeBridgeService.isInsideNativeWebView
    );
  }

  setVisible(visible: boolean): void {
    this.isOpen.set(visible);
  }

  private isAward$(): Observable<boolean> {
    return combineLatest([
      this.store$.select(selectActiveTab).pipe(startWith(null)),
      this.store$.pipe(cartData(), startWith(null)),
    ]).pipe(map(([tab, cart]) => tab === WidgetTab.AWARD || (cart ? isAwardBooking(cart.prices.unpaid) : false)));
  }
}
